import { z } from 'zod';

/**
 * Advisory-fee billing import — Bateman Capital Management ONLY.
 *
 * BCM bills advisory fees from a CSV produced outside this system: one row per
 * managed account, four columns. The importer turns each row into one invoice,
 * so a month's billing is a single upload rather than dozens of hand-keyed
 * invoices.
 *
 * The company restriction is not cosmetic. Every rule below (revenue always
 * 4009, A/R always 1008, deposits always 1005, memo = "Name - Account") is
 * specific to how BCM bills, and applying it to another company's books would
 * post to whatever those codes happen to mean there. Account codes are NOT
 * portable between companies in this system.
 */

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');

/**
 * Bateman Capital Management, Inc — the only company this feature serves.
 *
 * Shared so the server's gate and the UI's visibility test read the same value.
 * The server enforces it; the UI merely avoids showing a button that would 403.
 */
export const BILLING_COMPANY_ID = '28bafd86-55e2-46ef-9c8b-0d44dd6fdd56';

/** The four columns the CSV must supply, after header normalisation. */
export const BILLING_CSV_COLUMNS = ['Name', 'Account Number', 'Invoice Date', 'Total'] as const;

/** Revenue account every imported line credits. */
export const BILLING_REVENUE_CODE = '4009';
/** Receivable account every imported invoice debits. */
export const BILLING_AR_CODE = '1008';
/** Bank account the broker's monthly payment is deposited to. */
export const BILLING_DEPOSIT_CODE = '1005';
/** Payment terms stamped on every imported invoice (drives the due date). */
export const BILLING_TERMS = 'Net 30';

export const billingImportUploadSchema = z.object({
  /** Original filename, kept for the audit trail and error messages. */
  fileName: z.string().trim().min(1).max(255),
  /** base64-encoded CSV bytes (no data: prefix). */
  contentBase64: z.string().min(1),
});
export type BillingImportUpload = z.infer<typeof billingImportUploadSchema>;

/** One CSV row that parsed cleanly and is ready to become an invoice. */
export interface BillingImportRow {
  /** 1-based row number in the file, header included, so errors point at what the user sees. */
  rowNumber: number;
  name: string;
  accountNumber: string;
  invoiceDate: string;
  /**
   * What the invoice is for — always positive.
   *
   * The CSV is written from the client's point of view, where the fee is money
   * leaving their account and prints negative. The same fee is BCM's revenue,
   * so the magnitude is what gets billed.
   */
  total: string;
  /** The value exactly as the file stated it, sign included, for display. */
  rawTotal: string;
  /** Both the invoice memo and the line description: "Name - Account". */
  descriptor: string;
  /** Deterministic idempotency key — re-uploading the same file changes nothing. */
  externalId: string;
  /** True when an invoice with this externalId already exists at BCM. */
  alreadyImported: boolean;
}

/** A row that cannot become an invoice, and why. */
export interface BillingImportRowError {
  rowNumber: number;
  message: string;
  /** The raw cell values, so the user can see what the parser actually read. */
  raw: Record<string, string>;
}

export interface BillingImportPreview {
  fileName: string;
  rows: BillingImportRow[];
  errors: BillingImportRowError[];
  /** Sum of every importable row's total. */
  totalAmount: string;
  /** How many rows are replays of invoices already imported. */
  alreadyImportedCount: number;
  /**
   * True when the file states both negative and positive totals.
   *
   * A fee file is normally negative throughout (the client's view). A lone
   * positive among them is more likely a reversal or refund than a fee, and
   * taking its magnitude would bill the client for a credit — so this is
   * surfaced for a human to look at rather than quietly normalised.
   */
  mixedSigns: boolean;
  /** Resolved once, up front — a missing account blocks the whole import. */
  accounts: {
    revenue: { id: string; code: string; name: string };
    receivable: { id: string; code: string; name: string };
    deposit: { id: string; code: string; name: string };
  };
}

export interface BillingImportResult {
  created: number;
  skipped: number;
  totalAmount: string;
  invoiceIds: string[];
}

export const billingReceiveInputSchema = z.object({
  paymentDate: isoDate,
  /**
   * Invoices to settle. Explicit rather than "everything open" so a payment
   * that covers only part of the outstanding book cannot silently clear the
   * rest.
   */
  invoiceIds: z.array(z.string().uuid()).min(1),
});
export type BillingReceiveInput = z.infer<typeof billingReceiveInputSchema>;

export interface BillingReceiveResult {
  paidCount: number;
  totalAmount: string;
  /** Account the cash was debited to — always 1005 for BCM. */
  depositAccountCode: string;
}
